import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useBvaDetails } from 'api/queries/useBvaDetails'
import EmptyLogo from 'assets/images/bvaDetails/emptyLogo.png'
import { Toast } from 'components/Toast/Toast'
import { useToast } from 'components/Toast/useToast'
import { parseBvaDetailsParam } from 'utils/bvaSlug'

import { BvaDetailHeader } from './BvaDetailHeader'
import { ExploreMoreBvas } from './ExploreMoreBvas'
import { HumanTension } from './HumanTension'
import { exportBvaPdf } from './pdf/exportBvaPdf'
import './brandDetails.scss'

export const BrandDetails = () => {
  const { name } = useParams<{ name: string }>()
  const { id } = parseBvaDetailsParam(name)
  const { data, isLoading, isError } = useBvaDetails(id)

  const toast = useToast()
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success')
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const exportControllerRef = useRef<AbortController | null>(null)

  const notify = useCallback(
    (message: string, durationMs?: number) => {
      setToastVariant('success')
      toast.show(message, durationMs)
    },
    [toast],
  )

  // Navigating away mid-export: abort so the export's `finally` tears the
  // offscreen host down and stops touching state on an unmounted page.
  useEffect(
    () => () => {
      exportControllerRef.current?.abort()
    },
    [],
  )

  const handleDownloadPdf = useCallback(async () => {
    // Double-click guard via a ref, not `isExportingPdf`: state updates are
    // async, so two clicks in the same tick would both still see `false`.
    if (exportControllerRef.current || !data) return

    const controller = new AbortController()
    exportControllerRef.current = controller
    setIsExportingPdf(true)

    try {
      await exportBvaPdf(data, controller.signal)
      if (!controller.signal.aborted) notify('BVA PDF file successfully downloaded.')
    } catch (error) {
      if ((error as { name?: string } | undefined)?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('BVA PDF export failed', error)
        setToastVariant('error')
        toast.show("We couldn't generate the PDF. Please try again.", 5000)
      }
    } finally {
      exportControllerRef.current = null
      setIsExportingPdf(false)
    }
  }, [data, notify, toast])

  if (isLoading) {
    return <div className="bva-details-state body-m">Loading BVA details…</div>
  }

  if (isError || !data) {
    return <div className="bva-details-state body-m">We couldn't load this BVA. Please try again later.</div>
  }

  return (
    <>
      {data.backgroundUrl ? (
        <div
          className="bva-details__background"
          aria-hidden="true"
          style={{
            background: `linear-gradient(0deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0.75) 100%), url(${data.backgroundUrl}) lightgray 50% / cover no-repeat`,
          }}
        />
      ) : (
        <div
          className="bva-details__background"
          aria-hidden="true"
          style={{
            background: '#F1F1F1',
          }}
        />
      )}
      <div className="bva-details">
        <BvaDetailHeader
          logoUrl={data.logoUrl ? data.logoUrl : data.brandLogoUrl ? data.brandLogoUrl : EmptyLogo}
          year={data.year}
          category={data.category}
          brandName={data.brandName}
          isExportingPdf={isExportingPdf}
          onDownloadPdf={handleDownloadPdf}
          onNotify={notify}
        />
        <HumanTension sections={data.sections} />
        <ExploreMoreBvas currentBrandId={data.brandId} />
      </div>
      <Toast message={toast.message} isVisible={toast.isVisible} onClose={toast.hide} variant={toastVariant} />
    </>
  )
}
